import { z } from "zod";
import { operatorLocaleSchema, principalRoleSchema } from "./governance.js";
export const authSessionTokensSchema = z
    .object({
    tokenType: z.literal("Bearer"),
    idToken: z.string().min(1),
    refreshToken: z.string().min(1),
    expiresIn: z.number().int().positive(),
})
    .strict();
export const authLoginRequestSchema = z
    .object({
    email: z.string().trim().email(),
    password: z.string().min(1).max(128),
})
    .strict();
export const authRefreshRequestSchema = z
    .object({
    refreshToken: z.string().min(1).max(512),
})
    .strict();
export const authPasswordForgotRequestSchema = z
    .object({
    email: z.string().trim().email(),
    locale: operatorLocaleSchema.optional(),
})
    .strict();
export const authPasswordResetRequestSchema = z
    .object({
    token: z.string().min(16).max(256),
    password: z.string().min(8).max(128),
})
    .strict();
export const authInvitePreviewSchema = z
    .object({
    email: z.string().email(),
    displayName: z.string().min(1).optional(),
    role: principalRoleSchema,
    locale: operatorLocaleSchema,
    expiresAt: z.string().datetime(),
})
    .strict();
export const authInviteAcceptRequestSchema = z
    .object({
    password: z.string().min(8).max(128),
    displayName: z.string().trim().min(1).max(200).optional(),
})
    .strict();
/**
 * Google sign-in proves an operator's email. The API issues the normal
 * first-party session for an active operator whose email matches Google's
 * verified email. When the deployment restricts Google sign-in to Workspace
 * domains, a verified account of that domain with no operator row is
 * provisioned just in time as TAX_READER (owner decision 2026-09-28), and a
 * pending invite for the email is accepted instead.
 */
export const GOOGLE_LOGIN_CALLBACK_PATH = "/auth/callback";
export const authProvidersSchema = z
    .object({
    google: z
        .object({
        clientId: z.string().min(1),
        /** The public callback (tax.backchannel.com.br in Dolphin). */
        redirectUri: z.string().url(),
        /**
         * Every accepted callback, public first. A web app offers Google only
         * when `${location.origin}/auth/callback` is listed (for example a
         * local dev server allowed against Dolphin).
         */
        redirectUris: z.array(z.string().url()).min(1).optional(),
        /**
         * The single Workspace domain sign-in is restricted to, sent to Google
         * as the `hd` hint so its account chooser steers to that domain. Only a
         * hint: the API still enforces the domain and the `hd` claim.
         */
        hostedDomain: z.string().min(1).optional(),
    })
        .strict()
        .nullable(),
})
    .strict();
export const authGoogleSignInRequestSchema = z
    .object({
    code: z.string().min(1).max(2048),
    codeVerifier: z
        .string()
        .min(43)
        .max(128)
        .regex(/^[A-Za-z0-9._~-]+$/),
    nonce: z.string().min(16).max(256),
    redirectUri: z.string().url().max(512),
})
    .strict();
/**
 * EXISTING: an operator already existed. INVITE_ACCEPTED: a pending invite for
 * the email was accepted with its role. PROVISIONED: a TAX_READER operator was
 * created just in time.
 */
export const authGoogleSignInOutcomeSchema = z.enum(["EXISTING", "INVITE_ACCEPTED", "PROVISIONED"]);
export const authGoogleSignInResponseSchema = authSessionTokensSchema
    .extend({ outcome: authGoogleSignInOutcomeSchema })
    .strict();
//# sourceMappingURL=identity.js.map